import { NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { focusNextTabElement } from '../../functions/focus-next.functions';

export abstract class AbstractTypeaheadSearch<T> {
  value = '';
  searchTerm = '';
  showInputAfterSelect = true;
  minSearchLength = 1;
  debounceTime = 200;
  hasfocus = false;
  protected selectedItem: T;
  protected results: T[] = [];
  private _search: Observable<T[]>;

  protected constructor() {}

  get showNoResultText(): boolean {
    if (this.searchTerm.length === 0 || this.searchTerm.length < this.minSearchLength) {
      return false;
    }
    return this.results.length === 0;
  }

  setSearch = (dataObservable: Observable<T[]>) => {
    this._search = dataObservable;
  };

  public focus() {
    this.hasfocus = true;
  }

  onTab(event: Event): void {
    if (this.value) {
      event.preventDefault();
      focusNextTabElement();
    }
  }

  onClear(): void {
    this.selectedItem = null;
    this.searchTerm = '';
    this.value = '';
    this.results = [];
  }

  onSelected($event: NgbTypeaheadSelectItemEvent<T>): void {
    const selectedItem: T = $event.item;
    this.selectedItem = selectedItem;
    if (selectedItem) {
      this.value = this.formatEntry(selectedItem);
    }
  }

  public openTypeahead(inputElement: HTMLInputElement): void {
    if (!this.searchTerm && this.minSearchLength === 0) {
      inputElement.value = '';
      inputElement.focus();
      inputElement.dispatchEvent(new Event('input'));
    }
  }

  search = (search$: Observable<string>) => {
    return search$.pipe(
      debounceTime(this.debounceTime),
      distinctUntilChanged(),
      switchMap((searchTerm) => {
        this.searchTerm = searchTerm.toLowerCase();
        if (searchTerm?.length >= this.minSearchLength) {
          return this._search ? this._search : of([]);
        }
        return of([]);
      }),
      map((data: T[]) => {
        const results = data.filter(this.filterResults).sort(this.sortResults);
        this.results = results;
        return results;
      })
    );
  };

  abstract formatEntry(entry: T): string;

  protected inputFormatter = (input: any): string => {
    if (input && this.showInputAfterSelect) {
      return this.formatEntry(input);
    }
    return '';
  };

  protected resultFormatter = (result: T): string => {
    if (result) {
      return this.formatEntry(result);
    }
    return '';
  };

  protected filterResults = (result: T) => {
    return this.formatEntry(result).toLowerCase().includes(this.searchTerm.toLowerCase());
  };

  protected sortResults = (a: T, b: T) => {
    const aFormatted = this.formatEntry(a);
    const bFormatted = this.formatEntry(b);
    if (aFormatted < bFormatted) {
      return -1;
    }
    if (aFormatted > bFormatted) {
      return 1;
    }
    return 0;
  };
}
